import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLang } from "../contexts/LangContext";
import { IMAGES } from "../lib/images";
import { LOGO_MARK_AE } from "../lib/brand";
import useSEO from "../lib/useSEO";

// Map from string references in i18n to actual image URLs
const imageMap = {
  "IMAGES.hero": IMAGES.hero,
  "IMAGES.table": IMAGES.table,
  "IMAGES.medFood": IMAGES.medFood,
  "IMAGES.pizza1": IMAGES.pizza1,
  "IMAGES.pizza2": IMAGES.pizza2,
  "IMAGES.oven": IMAGES.oven,
  "IMAGES.edgarPizza": IMAGES.edgarPizza,
};

function resolveImage(ref) {
  if (!ref) return null;
  if (ref.startsWith("IMAGES.")) return imageMap[ref] || null;
  return ref; // direct path like "/annabell-edgar.jpg"
}

export default function About() {
  const { t } = useLang();
  const sections = t.about.sections || [];

  useSEO({
    title: "Über uns — Annabell & Edgar | Pizza Catering aus Hoyerswerda & Lohsa",
    description:
      "Wir sind Annabell & Edgar von Tæste. Aus Leidenschaft für handgemachte Pizza entstand unser mobiles Catering aus Lohsa für Hochzeiten, Firmenfeiern und Events in Hoyerswerda und der Lausitz.",
    keywords:
      "Über uns Tæste, Annabell und Edgar, Pizza Catering Hoyerswerda, Catering Lohsa, mobiles Pizza Catering, handgemachte Pizza Lausitz",
    path: "/ueber-uns",
  });

  return (
    <div data-testid="about-page" className="relative overflow-hidden">
      {/* Hero mit æ-Watermark (auf den Hero-Bereich begrenzt) */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 z-0 flex items-start justify-center"
        >
          <img
            src={LOGO_MARK_AE}
            alt=""
            data-testid="about-watermark"
            className="w-auto h-full max-h-full object-contain"
            style={{ opacity: 0.13 }}
          />
        </div>

        <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12 pt-20 md:pt-28 pb-16 md:pb-24 min-h-[45vh] md:min-h-[55vh] flex flex-col justify-center">
          <p className="eyebrow">{t.about.eyebrow}</p>
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl mt-3 leading-[1.02]" data-testid="about-page-title">
            {t.about.title}
          </h1>
        </div>
      </section>

      {/* Sektionen mit alternierendem Layout */}
      <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
        {sections.map((s, i) => {
          const imgSrc = resolveImage(s.image);
          const hasImage = Boolean(imgSrc);
          const reverse = Boolean(s.reverse);
          const landscape = Boolean(s.landscape);
          const textColSpan = hasImage ? (landscape ? "md:col-span-6" : "md:col-span-7") : "";
          const imageColSpan = landscape ? "md:col-span-6" : "md:col-span-5";
          const aspectClass = s.aspectClass || (landscape ? "aspect-[4/3]" : "aspect-[4/5]");
          return (
            <section
              key={i}
              data-testid={`about-section-${i}`}
              className="py-16 md:py-24 border-t border-border first:border-t-0"
            >
              <div
                className={`grid gap-10 md:gap-16 items-center ${
                  hasImage ? "md:grid-cols-12" : "md:grid-cols-1"
                }`}
              >
                <div
                  className={`${
                    hasImage
                      ? reverse
                        ? `${textColSpan} md:order-2`
                        : textColSpan
                      : "max-w-3xl"
                  }`}
                >
                  <h2 className="font-display text-3xl md:text-5xl leading-[1.08]" data-testid={`about-section-heading-${i}`}>
                    {s.heading}
                  </h2>
                  <div className="mt-6 space-y-5 text-base md:text-lg text-[hsl(var(--foreground))]/85 leading-relaxed">
                    {s.body.map((p, pi) => (
                      <p key={pi}>{p}</p>
                    ))}
                  </div>
                  {s.cta && (
                    <Link
                      to={s.cta.href}
                      data-testid={`about-cta-${i}`}
                      className="mt-8 inline-flex items-center gap-2 px-6 py-3 text-xs tracking-[0.22em] uppercase font-semibold bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--accent))] pill"
                    >
                      {s.cta.label} <ArrowRight size={14} />
                    </Link>
                  )}
                </div>

                {hasImage && (
                  <div className={`${imageColSpan} ${reverse ? "md:order-1" : ""}`}>
                    <div className={`${aspectClass} overflow-hidden blob-organic bg-[hsl(var(--secondary))]`}>
                      <img
                        src={imgSrc}
                        alt={s.alt || ""}
                        data-testid={`about-section-image-${i}`}
                        className="w-full h-full object-cover"
                        style={{ objectPosition: s.objectPos || "center" }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
